import { I18n } from '../helpers/i18n.mjs';
import { create, registerCustomElement } from '../utils.mjs';
import { ScenarioListInput } from './scenario-list.mjs';
import Loader from '../loader.mjs';
import FormanConfig from '../formanConfig.mjs';
import common from '../common.module.scss';

type ScenarioInputValue = { id: number; approvalMode?: 'auto-run' | 'approval-required' };
type ScenarioData = {
	id: number;
	name: string;
	isActive: boolean;
	description?: string;
	usePackages: string[];
	type: 'scenario' | 'module';
};

const config = FormanConfig.config.fields.aiagent;

const patchScenarios = async (agentId: number, scenarios: ScenarioInputValue[], teamId: number) => {
	const agentScenarios = scenarios.map(({ id, ...rest }) => ({ makeScenarioId: id, ...rest }));

	await Loader.guardedLoad(
		config.scenariolist.patch.path,
		{
			method: 'PATCH',
			body: JSON.stringify({
				scenarios: agentScenarios,
			}),
			context: {
				agentId,
				teamId,
			},
		},
		true,
	);
};

type ConfirmationModalType = HTMLElement & {
	open(): Promise<boolean>;
};

const waitForDeleteConfirmation = async (scenario: ScenarioData) => {
	const ConfirmationModal = window.customElements.get('imt-confirmation-modal');

	if (!ConfirmationModal) {
		return true;
	}

	const body = create('span');
	body.innerHTML = I18n.l('aiAgentTools.confirmDelete.body', { scenarioName: scenario.name });

	const confirmation = new ConfirmationModal({
		title: I18n.l('aiAgentTools.confirmDelete.title'),
		body,
		cancelButton: I18n.l('aiAgentTools.confirmDelete.cancel'),
		actionButton: I18n.l('aiAgentTools.confirmDelete.delete'),
		actionType: 'delete',
	}) as ConfirmationModalType;

	return await confirmation.open();
};

export class AiAgentToolsInput extends ScenarioListInput {
	private agentId: number | null = null;

	override async _build(instructions: { agentId?: number }, value: unknown, metadata: unknown) {
		if (!instructions.agentId) {
			console.warn('AiAgentToolsInput requires an agentId');
			return;
		}

		const { agentId, ...scenarioListInstructions } = instructions;
		this.agentId = agentId;

		await super._build(scenarioListInstructions, value, metadata);

		this.renderToolPills();
	}

	private renderToolPills(): void {
		for (const scenario of this.scenariosCache) {
			if (scenario?.type === 'tool' && scenario?.id) {
				const item = this.querySelector(`imt-scenario-list-item[data-id="${scenario.id}"]`);
				const pill = item?.querySelector(`.${common.pill}`);
				if (pill) {
					pill.textContent = scenario.id;
					pill.classList.add(common.pillNeutral);
				}
			}
		}
	}

	override async patchScenarioList(
		newScenarios: ScenarioInputValue[],
		type: 'removed' | 'added',
		scenario: ScenarioData,
	): Promise<boolean> {
		this.setErrorMessage(null);

		if (!this.agentId || !this.form?.meta?.teamId) {
			return false;
		}

		let accepted = true;
		if (type === 'removed') {
			accepted = await waitForDeleteConfirmation(scenario);
		}

		if (!accepted) {
			return false;
		}

		this.classList.add('loading');
		try {
			await patchScenarios(this.agentId, newScenarios, this.form.meta.teamId);
			return true;
		} catch (e) {
			console.error('Failed to patch ai agent tools:', e);
			this.setErrorMessage(I18n.l('aiAgentTools.patchErrorMessage'));

			return false;
		} finally {
			this.classList.remove('loading');
		}
	}

	/**
	 *  Add a new banner to the config.
	 * @param {Array<object>} addScenarioConfig
	 */
	override extendAddScenarioConfig(addScenarioConfig?: Array<object>) {
		if (addScenarioConfig?.length) {
			addScenarioConfig.push({
				type: 'banner',
				theme: 'warning',
				text: I18n.l('aiAgentTools.addedToAllAgentsWarning'),
			});
		}
	}
}

registerCustomElement('imt-input-aiagenttools', AiAgentToolsInput);
